import React, { memo } from 'react'
import { connect, useSelector } from 'react-redux'
import { compose } from '@cbgms/base/utils/compose'
import { Route, Switch } from 'react-router-dom'
import { IRootReducerState } from 'state/rootReducer'
import { useShopTheme } from 'app/auth/hooks/use-shop-theme'
import { useCurrentEmployee } from 'app/employees/hooks/use-current-employee'
import { ThemeReducerState } from './auth/state/reducers/theme'
import { ThemeProvider, calculateCssVars } from 'utils/theme'
import cssVars from 'css-vars-ponyfill'
import BreadCrumbDataProviderContainer from 'app/crumbs/context/BreadCrumbDataProviderContainer'
import { Provider } from '@cbgms/base/shop-country'
import { useTranslation } from 'react-i18next'
import lazyWithReload from 'utils/lazy-with-reload'
import { PermissionExtensionContextProvider } from 'contexts/PermissionExtensionContext/PermissionExtensionContext'
import OnboardingRedirect from './onboarding/OnboardingRedirect'
import { ShopSession } from './ShopSession'
import { GMSDataGridConfigProvider } from 'contexts/ListConfig/GMSDataGridConfigProvider'
import { SettingsContextProvider } from 'contexts/Settings/SettingsContext'
import ModulesContainer from 'app/Container'

const _OnboardingContainer = lazyWithReload(() => import(/* webpackPrefetch: true */ 'app/onboarding/OnboardingContainer'))
const OnboardingContainer = memo(_OnboardingContainer)

interface MapStateToProps {
  theme: ThemeReducerState
  shopID: string
}

const mapStateToProps = (state: IRootReducerState): MapStateToProps => ({
  theme: state.auth.theme,
  shopID: state.auth.currentShop
})

const enhance = compose<MapStateToProps, {}>(connect(mapStateToProps))

export const Private: React.FC<MapStateToProps> = props => {
  const { theme, shopID } = props
  const { i18n } = useTranslation()
  // Don't block the dashboard shell on this - fall back to `theme` until it resolves
  const { data: shopTheme } = useShopTheme()
  const globalLocale = useSelector((state: IRootReducerState) => state.auth.localisation.Locale)

  // Use React Query cache key to prevent duplicate API calls
  useCurrentEmployee()

  React.useEffect(() => {
    if (!globalLocale) {
      return
    }

    // Pre-load the language bundle before switching, otherwise i18next's Suspense mode
    // suspends every translation consumer app-wide (hiding the whole dashboard) while it downloads
    let cancelled = false
    i18n.loadLanguages(globalLocale).then(() => {
      if (!cancelled) {
        i18n.changeLanguage(globalLocale)
      }
    })

    return () => {
      cancelled = true
    }
  }, [globalLocale, i18n])

  React.useEffect(() => {
    const effectiveTheme = shopTheme || theme
    cssVars({
      variables: calculateCssVars(effectiveTheme)
    })
  }, [shopTheme, theme])

  return (
    <ThemeProvider value={shopTheme || theme}>
      <Provider shopID={shopID}>
        <SettingsContextProvider>
          <PermissionExtensionContextProvider>
            <ShopSession>
              <BreadCrumbDataProviderContainer>
                <GMSDataGridConfigProvider>
                  <OnboardingRedirect />
                  <Switch>
                    <Route path='/onboarding' render={() => <OnboardingContainer />} />
                    <Route path='/' render={() => <ModulesContainer />} />
                  </Switch>
                </GMSDataGridConfigProvider>
              </BreadCrumbDataProviderContainer>
            </ShopSession>
          </PermissionExtensionContextProvider>
        </SettingsContextProvider>
      </Provider>
    </ThemeProvider>
  )
}

export default enhance(Private)
